import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { rawThemeAtom, themeAtom } from "@humansignal/ui";

/**
 * ThemeSync is the always-mounted theme controller.
 *
 * Background: ``ThemeSegment`` (in the user-profile dropdown) was the
 * only mounted component that wrote ``data-color-scheme`` on <html>
 * post-boot-script. Until the user opened that dropdown, the
 * attribute could drift out of sync with the user's preference —
 * especially after a route change or React re-render that touched
 * <html>. ThemeSync lives at the app root and is responsible for
 * keeping <html data-color-scheme> in lockstep with the resolved
 * theme on every change. Renders nothing.
 *
 * IMPORTANT atom-value contract: ``themeAtom`` holds the RESOLVED
 * value ("Light" / "Dark") — never "Auto". ``useTokenColor`` reads
 * the atom and normalises ``.toLowerCase() === "dark" ? "dark" :
 * "light"``, so a raw "Auto" in the atom would always read as
 * "light" and break theming for Auto-mode users on a dark OS. The
 * RAW preference ("Auto" / "Light" / "Dark") lives in localStorage;
 * the RESOLVED value lives in the atom. An earlier draft of this
 * component wrote the raw value into the atom — that was the bug.
 */

const PREFERRED_COLOR_SCHEME_KEY = "preferred-color-scheme";

const systemTheme = (): "Light" | "Dark" =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "Dark"
    : "Light";

/**
 * Returns the RESOLVED theme value for the raw preference + current
 * OS. ``null`` / ``undefined`` / "Auto" resolve to the OS preference;
 * "Light" / "Dark" pass through.
 */
const resolve = (raw: string | null | undefined): "Light" | "Dark" => {
  if (raw === "Light" || raw === "Dark") return raw;
  return systemTheme();
};

const applyAttribute = (resolved: "Light" | "Dark") => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-color-scheme", resolved.toLowerCase());
};

export const ThemeSync = () => {
  // Re-render when ThemeSegment (or anywhere else) writes a new
  // value to the atom. That re-render fires the effects below.
  const theme = useAtomValue(themeAtom);
  const setTheme = useSetAtom(themeAtom);
  const setRawTheme = useSetAtom(rawThemeAtom);

  // Effect A: keep the <html> attribute synced with the atom on
  // every change. The atom is the source of truth at runtime.
  useEffect(() => {
    const t = theme as string;
    // Defensive: if the atom somehow holds "Auto" (e.g. older
    // ThemeSync wrote it before this fix, or a future caller writes
    // raw), normalise via setTheme + bail; the re-render with the
    // resolved value re-fires this effect and applies correctly.
    if (t !== "Light" && t !== "Dark") {
      setTheme(resolve(t));
      return;
    }
    applyAttribute(t);
  }, [theme, setTheme]);

  // Effect B: watch the OS theme. Only meaningful when the user's
  // raw preference is "Auto" — flipping the OS theme should update
  // <html> live in that mode.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const raw = window.localStorage.getItem(PREFERRED_COLOR_SCHEME_KEY) || "Auto";
    if (raw !== "Auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setTheme(resolve("Auto"));
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mq as any).addListener(onChange);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => (mq as any).removeListener(onChange);
  }, [setTheme]);

  // Effect C: pick up theme changes from OTHER tabs / windows. The
  // storage event fires in tab B when tab A writes to localStorage.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== PREFERRED_COLOR_SCHEME_KEY) return;
      setRawTheme(e.newValue || "Auto");
      setTheme(resolve(e.newValue));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [setTheme, setRawTheme]);

  // Effect D: re-sync from localStorage on bfcache restore + every
  // ``pageshow`` event. THIS is the fix for the "theme flips on back
  // button" bug: when we return to the SPA from a Django-served page,
  // ``base.html``'s inline script may have already re-resolved the
  // attribute (e.g. Auto + a different OS pref) and disagreed with
  // the atom's cached value. Reading localStorage on pageshow keeps
  // the atom in lockstep with what actually painted the page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPageShow = () => {
      const raw = window.localStorage.getItem(PREFERRED_COLOR_SCHEME_KEY) || "Auto";
      setRawTheme(raw);
      setTheme(resolve(raw));
    };
    // Also run once on mount so a hot reload / initial hydrate picks
    // up the current localStorage value even if the atom was seeded
    // before storage was ready (rare, but real under strict mode).
    onPageShow();
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [setTheme, setRawTheme]);

  return null;
};
